import React, { useState } from 'react';
import { Package, Pencil, Trash2 } from 'lucide-react';
import { useData } from '../context/DataContext';
import { fmtRM2 } from '../lib/store';
import { PageHeader, EmptyState, Card } from '../components/Shared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';

const empty = { name: '', description: '', unit: 'unit', price: 0, taxable: true };

export default function Products() {
  const { products, addItem, updateItem, removeItem } = useData();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm(p); setEditId(p.id); setOpen(true); };

  const save = () => {
    if (!form.name.trim()) { toast.error('Item name is required'); return; }
    const payload = { ...form, price: Number(form.price) || 0 };
    if (editId) { updateItem('products', editId, payload); toast.success('Item updated'); }
    else { addItem('products', payload); toast.success('Item added'); }
    setOpen(false);
  };

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Products / Services" subtitle="Manage item list, pricing, taxes and units." actionLabel="Add Item" onAction={openNew} />

      {products.length === 0 ? (
        <EmptyState icon={Package} title="No items yet" subtitle="Add your first product or service." />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Item</TableHead>
                <TableHead className="font-semibold text-slate-600">Unit</TableHead>
                <TableHead className="font-semibold text-slate-600">SST</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Price</TableHead>
                <TableHead className="w-24 text-right font-semibold text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600"><Package className="h-4 w-4" /></div>
                      <div>
                        <p className="font-medium text-slate-800">{p.name}</p>
                        <p className="text-xs text-slate-400">{p.description}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><span className="text-sm text-slate-600">/ {p.unit}</span></TableCell>
                  <TableCell>{p.taxable ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">6% SST</Badge> : <Badge variant="outline" className="text-slate-500">Exempt</Badge>}</TableCell>
                  <TableCell className="text-right font-semibold text-slate-800">{fmtRM2(p.price)}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(p)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => { removeItem('products', p.id); toast.success('Item removed'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Edit Item' : 'Add Item'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Item Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Unit</Label><Input className="mt-1.5" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            <div><Label>Price (RM)</Label><Input type="number" className="mt-1.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch checked={form.taxable} onCheckedChange={(v) => setForm({ ...form, taxable: v })} />
              <Label className="cursor-pointer">Subject to 6% SST</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save}>{editId ? 'Save Changes' : 'Add Item'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
